// src/pages/bloomterminal/alerts/steps/CommoditySelector.jsx
import React, { useState, useRef } from 'react';
import { ChevronUp, Loader2 } from 'lucide-react';
import { useCommodities } from '../../../utils/api-hooks/commodity';
import { useGlobalFontSize } from '../../../context/GlobalFontSizeContext';

const CommoditySelector = ({ selectedCommodity, onCommodityToggle, className = '' }) => {
  const { fontSize } = useGlobalFontSize();
  const [isSelectorOpen, setIsSelectorOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('Fruits'); // Default to Fruits
  const selectorRef = useRef(null);

  // Fetch commodities using custom hook
  const { commodities, isLoading, isError, error } = useCommodities();

  const handleCommoditySelect = (commodity) => {
    onCommodityToggle(commodity);
  };

  const CommodityCheckbox = ({ commodity, checked, onChange }) => {
    const { fontSize: checkboxFontSize } = useGlobalFontSize();
    return (
      <div className="flex items-start gap-2 cursor-pointer" onClick={onChange}>
        <div
          className={`w-4 h-4 rounded-full border-2 flex items-center justify-center transition-colors flex-shrink-0 mt-0.5 ${
            checked
              ? 'border-bloom-green bg-bloom-green/10'
              : 'border-[#828382] bg-transparent'
          }`}
        >
          {checked && (
            <div className="w-2 h-2 rounded-full bg-bloom-green" />
          )}
        </div>
        <span 
          className="font-['Inter'] font-normal text-[#282A29] dark:text-[#f0f5f2] break-words line-clamp-2"
          style={{ fontSize: checkboxFontSize - 2 }}
        >
          {commodity}
        </span>
      </div>
    );
  };

  // Sort categories alphabetically
  const categoryKeys = Object.keys(commodities).sort((a, b) =>
    a.toLowerCase().localeCompare(b.toLowerCase())
  );

  // Sort commodities within the selected category alphabetically
  const currentCommodities = [...(commodities[selectedCategory] || [])].sort((a, b) =>
    a.toLowerCase().localeCompare(b.toLowerCase())
  );

  return (
    <div className={`flex flex-col gap-1 w-full ${className}`} ref={selectorRef}>
      {/* Commodity Selector Button */}
      <div className="flex flex-col gap-1">
        <label 
          className="font-['Inter'] font-normal text-[#282A29] dark:text-[#f0f5f2]"
          style={{ fontSize: fontSize - 2 }}
        >
          Commodity
        </label>
        <button
          onClick={() => setIsSelectorOpen(!isSelectorOpen)}
          disabled={isLoading}
          className={`w-full max-w-[200px] h-8 px-3 py-2 border border-bloom-green rounded-[8px] bg-transparent flex items-center justify-between font-['Inter'] font-medium text-[#828382] dark:text-[#5d625f] transition-colors ${selectedCommodity ? "text-[#282A29] dark:text-[#f0f5f2]": ""} ${isLoading ? "opacity-50 cursor-not-allowed" : ""}`}
          style={{ fontSize: fontSize - 2 }}
        >
          <span className="truncate">
            {isLoading ? 'Loading...' : (selectedCommodity || 'Select...')}
          </span>
          {isLoading ? (
            <Loader2 size={16} className="text-[#828382] dark:text-[#5d625f] animate-spin flex-shrink-0 ml-2" />
          ) : (
            <ChevronUp
              size={16}
              className={`text-[#828382] dark:text-[#5d625f] transition-transform flex-shrink-0 ml-2 ${
                isSelectorOpen ? 'rotate-180' : ''
              }`}
            />
          )}
        </button>
      </div>

      {/* Commodities Selector - Only show when button is clicked */}
      {isSelectorOpen && (
        <div className="relative border border-bloom-green rounded-[8px] min-h-[200px] max-h-[350px] overflow-hidden flex w-full">
          {/* Loading State */}
          {isLoading && (
            <div role="status" aria-busy="true" aria-label="Loading" className="absolute inset-0 z-20 flex flex-col gap-2 p-3 bg-[#F0F5F2] dark:bg-[#191919]">
              <div className="skeleton-sheen h-8 w-full rounded-[8px]" />
              <div className="skeleton-sheen h-8 w-full rounded-[8px]" />
              <div className="skeleton-sheen h-8 w-full rounded-[8px]" />
            </div>
          )}

          {/* Error State */}
          {isError && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/50 z-20">
              <div className="text-center px-4">
                <p 
                  className="font-['Inter'] text-red-400 mb-2"
                  style={{ fontSize: fontSize }}
                >
                  Failed to load commodities
                </p>
                <p 
                  className="font-['Inter'] text-[#282A29] dark:text-[#828382]"
                  style={{ fontSize: fontSize - 2 }}
                >
                  {error?.message || 'Please try again'}
                </p>
              </div>
            </div>
          )}

          {/* Category Labels - Left Side */}
          <div className="flex-shrink-0 w-[80px] sm:w-[100px] py-4 px-3 border-r border-bloom-green/20 overflow-y-auto">
            <div className="flex flex-col gap-2">
              {categoryKeys.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`font-['Inter'] whitespace-pre-wrap text-left transition-colors ${
                    selectedCategory === category
                      ? 'font-semibold text-[#282A29] dark:text-[#f0f5f2]'
                      : 'font-normal text-[#828382] hover:text-[#282A29] dark:hover:text-[#f0f5f2]'
                  }`}
                  style={{ fontSize: fontSize - 2 }}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>

          {/* Scrollable Content - Responsive Grid with minmax */}
          <div className="flex-1 overflow-y-auto overflow-x-hidden p-4 min-w-0 commodity-scroll">
            <style>{`
              .commodity-scroll::-webkit-scrollbar {
                width: 4px;
              }
              .commodity-scroll::-webkit-scrollbar-track {
                background: transparent;
              }
              .commodity-scroll::-webkit-scrollbar-thumb {
                background: #828382;
                border-radius: 4px;
              }
              .commodity-scroll::-webkit-scrollbar-thumb:hover {
                background: #5d625f;
              }
            `}</style>
            {/* Vertical list layout */}
            <div 
              className="flex flex-col gap-y-3"
            >
              {currentCommodities.map((commodity, index) => (
                <CommodityCheckbox
                  key={`${selectedCategory}-${index}`}
                  commodity={commodity}
                  checked={selectedCommodity === commodity}
                  onChange={() => handleCommoditySelect(commodity)}
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CommoditySelector;
